import React, { useState, useEffect } from 'react';
import '../../../Styles/Payment/PaymentForm.css';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../Notification/NotificationContext';
import {
    useStripe,
    useElements,
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement,
} from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import axiosUserInstance from '../../../axiosUserInstance';
import axiosVendorInstance from '../../../axiosVendorInstance';
import axiosPaymentInstance from '../../../axiosPaymentinstance';
import { useCart } from '../../../CartProvider';
import visaImg from '../../../assets/cards/visaCard.png';
import dinersImg from '../../../assets/cards/dinersClubCard.png';
import jcbImg from '../../../assets/cards/jcbCard.png';
import mastercardImg from '../../../assets/cards/masterCard.png';
import unionPayImg from '../../../assets/cards/unionpayCard.png';
import amexImg from '../../../assets/cards/amexCard.png';
import discoverImg from '../../../assets/cards/discoverCard.png';
import defaultImg from '../../../assets/svg/defaultCardImg.svg';
import addCardImage from '../../../assets/svg/addCardIcon.svg';
import Loader from '../../../Components/Loader/Loader';
import DeleteIconSvg from '../../../assets/svg/deleteIcon';
import DeleteOfferModal from '../../Vendor/DeleteOfferModal';
import CloseIcon from '../../../assets/svg/closeIcon';

const CARD_ELEMENT_OPTIONS = {
    showIcon: true,
    style: {
        base: {
            fontSize: '16px',
            color: '#424770',
            letterSpacing: '0.025em',
            fontFamily: 'Source Code Pro, monospace',
            '::placeholder': {
                color: '#aab7c4',
            },
        },

        invalid: {
            color: '#9e2146',
        },
    },
};

// eslint-disable-next-line no-unused-vars
const PaymentForm = ({
    total,
    couponsID,
    offersID,
    cookingInstructionValue,
    deliveryInstructionValue,
    selectedCoupon,
    discount,
    selectedOption,
    vendorAddress,
    selectedAddress,
    calculationLoading,
    taxAmount,
    platFormFee,
    deliveryInfo,
    finalAmount,
    taxesAndOtherfees,
    deliveryFee
}) => {
    const selectedOptionID = selectedOption === 'delivery' ? 2 : 1;
    const {fetchCartCount, setOfferModelPercentage} = useCart();

    const token = localStorage.getItem('authToken');
    const decodedToken = jwtDecode(token);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [cartData, setCartData] = useState([]);
    const [vendorID, setVendorID] = useState(null);
    const [showCardDetails, setShowCardDetails] = useState(false);
    const [selectedCard, setSelectedCard] = useState('');
    const [loading, setLoading] = useState(false);
    const [savedCards, setSavedCards] = useState([]);
    const [cardHolderError, setCardHolderError] = useState('');
    const [expiryError, setExpiryError] = useState('');
    const [cvcError, setCvcError] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('card');
    const [mobileNumber, setMobileNumber] = useState();
    const [cardHolderName, setCardHolderName] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const { addNotification } = useNotifications();
    const stripe = useStripe();
    const elements = useElements();

    const fetchDataFromCart = async () => {
        const location = JSON.parse(localStorage.getItem('selectedLocation'));

        const lat = location?.lat;
        const lng = location?.lng;
        const itemResponse = await axiosUserInstance.get('/cartdetails/cartsp', {
            params: {
                latitude: lat,
                longitude: lng,
            },
        });

        setCartData(itemResponse?.data?.cartDetails[0]?.Cart_Information);
        setVendorID(
            itemResponse?.data?.cartDetails[0]?.Cart_Information[0]?.Vendor_Id
        );
    };

    useEffect(() => {
        fetchDataFromCart();
    }, []);

    const fetchSavedCards = async () => {
        try {
            setIsLoading(true);
            const response = await axiosPaymentInstance.get('/payment/save-card-db');
            setIsLoading(false);
            if (response?.data?.success) {
                setSavedCards(response?.data?.cards);
            } else {
                console.error('Error fetching saved cards');
            }
        } catch (error) {
            setIsLoading(false);
            console.error('Error fetching saved cards:', error);
        }
    };

    useEffect(() => {
        fetchSavedCards();
    }, []);

    const handleSaveCard = async () => {
        if (!stripe || !elements) return;
        setLoading(true);
        try {
            const cardNumberElement = elements.getElement(CardNumberElement);
            const cardExpiryElement = elements.getElement(CardExpiryElement);
            const cardCvcElement = elements.getElement(CardCvcElement);

            if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
                console.error('Missing Stripe Elements');
                setLoading(false);
                return;
            }
            const { paymentMethod, error } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardNumberElement,
                billing_details: {
                    name: cardHolderName,
                },
            });

            if (error) {
                console.error('Error creating payment method:', error);
                toast.error(error.message || 'Failed to create payment method.');
                setLoading(false);
                return;
            }

            const response = await axiosPaymentInstance.post(
                '/payment/save-cardinfo',
                {
                    paymentMethodId: paymentMethod.id,
                }
            );

            if (response?.data?.success) {
                await fetchSavedCards();
                setSavedCards((prevCards) => {
                    const newCards = [...prevCards];
                    const newCard = newCards[newCards.length - 1];
                    if (newCard) {
                        setSelectedCard(newCard.stripe_card_id);
                    }
                    return newCards;
                });
                setShowCardDetails(false);
                toast.success('Card saved successfully!');
            } else {
                toast.error('Error saving card: ' + response?.data?.error);
            }
        } catch (err) {
            console.error('Error saving card:', err);
            toast.error('Failed to save card Please enter valid card number.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const checkMobileNumber = async () => {
            try {
                const res = await axiosUserInstance.get('/customers/checkmobilenumber');
                setMobileNumber(res?.data?.mobile_number);
            } catch (error) {
                console.error('API error:', error);
            }
        };

        checkMobileNumber();
    }, []);

    const handlePayment = async () => {
        setShowCardDetails(false);
        if (paymentMethod === 'card') {
            if (!stripe || !elements || !selectedCard) {
                return;
            }

            setIsProcessing(true);

            try {
                setLoading(true);

                let customerAddress;

                if (selectedOption === 'pickup') {
                    const vendorAddr =
                vendorAddress || JSON.parse(localStorage.getItem('vendorAddress'));

                    customerAddress = {
                        line1: vendorAddr?.vendor_address || '',
                        city: vendorAddr?.vendor_city || '',
                        state: vendorAddr?.vendor_state || '',
                        postal_code: vendorAddr?.vendor_postalcode || '',
                        country: 'US',
                    };
                } else {
                    const selectedAddr =
                selectedAddress || JSON.parse(localStorage.getItem('selectedCustomerAddress'));

                    customerAddress = {
                        line1: selectedAddr?.Address_line_1 || '',
                        city: selectedAddr?.City || '',
                        state: selectedAddr?.State || '',
                        postal_code: selectedAddr?.ZIP || '',
                        latitude: selectedAddr?.Latitude || '',
                        longitude: selectedAddr?.Longitude || '',
                        country: 'US',
                    };
                }

                const response = await axiosPaymentInstance.post('/payment/pay', {
                    paymentMethodId: selectedCard,
                    amount: total * 100,
                    currency: 'usd',
                    customerDetails: {
                        address: customerAddress,
                        name: decodedToken?.username,
                        phone: mobileNumber || '',
                    },
                    vendorId: vendorID,
                    cartItems: cartData?.map((item) => ({
                        Item_ID: item?.Item_ID,
                        Item_Quantity: item?.Item_Quantity,
                        Total_Price: item?.Total_Price,
                        MenuItem_Description: item?.MenuItem_Description,
                    })),
                    address: customerAddress,
                    deliveryInstructions: deliveryInstructionValue,
                    cookingInstructions: cookingInstructionValue,
                    orderTypeId: selectedOptionID,
                    tax: taxAmount,
                    PlatFormFee:platFormFee,
                    discount: discount,
                    offerId: offersID || null,
                    couponCode: selectedCoupon || null,
                    deliveryStatus:selectedOption,
                    deliveryResponse:deliveryInfo,
                    finalAmount:finalAmount,
                    taxesAndOtherfees:taxesAndOtherfees,
                    deliveryFee:deliveryFee
                });

                if (response?.data?.success) {
                    const { orderId } = response.data;
                    console.log(orderId,'orderId');
                    // ✅ PaymentIntent created and stored successfully
                    // You will confirm it later on server (e.g. after vendor accepts)

                    const notificationPayload = {
                        from_user_id: decodedToken?.id,
                        to_user_id: vendorID,
                        type: 'order_placed',
                        action_id: vendorID,
                        notification_boundary: 'private',
                        is_read: false,
                    };

                    await addNotification(notificationPayload);

                    const vendorNotificationPayload = {
                        from_user_id: decodedToken?.id,
                        type: 'vendor_order_type',
                        action_id: vendorID,
                        notification_boundary: 'private',
                        is_read: false,
                        notes: `You have received a new order from ${decodedToken?.username}`,
                    };

                    await addNotification(vendorNotificationPayload);

                    setCartData([]);
                    fetchCartCount();
                    setOfferModelPercentage(null);
                    navigate('/PaymentSuccess', { state: { orderId } });
                } else {
                    toast.error(response?.data?.error || 'Payment Intent creation failed.');
                    setIsProcessing(false);
                    return;
                }
            } catch (err) {
                console.error('Error during payment:', err);
                toast.error('Error during payment. Please try again.');
                setIsProcessing(false);
                navigate('/PaymentFail');
            } finally {
                setLoading(false);
            }

            try {
                await axiosUserInstance.delete('/cartdetails/customer/delteallByCustomerid');
            } catch (error) {
                console.error(error);
            }

            setIsProcessing(false);
        }
        else if (paymentMethod === 'cash') {
            try {
                const getPaymentTypeId = () => {
                    if (paymentMethod === 'cash') return 1;
                    if (paymentMethod === 'card') return 2;
                    if (paymentMethod === 'other') return 3;
                    return null;
                };

                // Order data, you may get this from the form or app state
                const orderData = {
                    Order_Info: 'Payment success and order placed',
                    Order_type_id: selectedOptionID,
                    Payment_type_id: getPaymentTypeId(),
                    Total_price: total,
                    Comments: 'Please deliver by 6 PM',
                    Status_id: 1,
                    Created_by: 3,
                };

                // Mapping through the cart items and structuring itemsData
                const itemsData =
          cartData?.map((item) => ({
              Price: item.Total_Price,
              Quantity: item.Item_Quantity,
              Description: item.MenuItem_Description,
              Instructions: 'No bell peppers',
              Menu_item_id: item.Item_ID,
          })) || [];

                // Prepare the payload
                const dataToSend = {
                    cookingInstructionValue: cookingInstructionValue,
                    deliveryInstructionValue: deliveryInstructionValue,
                    orderData,
                    itemsData,
                };

                await axiosVendorInstance
                    .post(
                        `/Order/vendor/${vendorID}/user/${decodedToken?.id}`,
                        dataToSend
                    )
                    .then(() => {
                        setOfferModelPercentage(null);
                        fetchCartCount();
                    });
                const notificationPayload = {
                    from_user_id: decodedToken.id,
                    to_user_id: vendorID,
                    type: 'order_placed',
                    action_id: vendorID,
                    notification_boundary: 'private',
                    is_read: false,
                };

                await addNotification(notificationPayload);

                // Create the second notification specifically for the vendor
                const vendorNotificationPayload = {
                    from_user_id: decodedToken.id,
                    type: 'vendor_order_type',
                    action_id: vendorID,
                    notification_boundary: 'private',
                    is_read: false,
                    notes: `You have received a new order from ${decodedToken.username}`,
                };

                await addNotification(vendorNotificationPayload);
            } catch (error) {
                console.error('There was an error placing the order:', error);
                toast.error('failed to place the order');
            }

            if (couponsID) {
                const couponData = {
                    coupon_id: couponsID,
                    user_id: decodedToken?.id,
                    amount: total,
                };

                await axiosVendorInstance.post(
                    `/vendor/couponredeption/${vendorID}`,
                    couponData,
                    { skipAuth: true }
                );
            } else if (offersID) {
                const offerData = {
                    offer_id: offersID,
                    user_id: decodedToken?.id,
                    amount: total,
                };

                await axiosVendorInstance.post(
                    `/vendor/offers/vendor/${vendorID}`,
                    offerData,
                    { skipAuth: true }
                );
            } else {
                console.log('No coupon or offer selected');
            }

            try {
                await axiosUserInstance.delete(
                    '/cartdetails/customer/delteallByCustomerid'
                );
            } catch (error) {
                console.error(error);
            }

            setCartData([]);
            navigate('/PaymentSuccess');
            setIsProcessing(false);
        }
    };

    const handlePaymentMethodChange = (method) => {
        setPaymentMethod(method);
        if (method === 'addCard') {
            setPaymentMethod('card');
            setShowCardDetails(true);
        } else if (method === 'card') {
            setShowCardDetails(false);
        }
    };

    const getCardBrandIcon = (brand) => {
        switch (brand.toLowerCase()) {
        case 'visa':
            return visaImg;
        case 'mastercard':
            return mastercardImg;
        case 'amex':
            return amexImg;
        case 'discover':
            return discoverImg;
        case 'jcb':
            return jcbImg;
        case 'diners':
            return dinersImg;
        case 'unionpay':
            return unionPayImg;
        default:
            return defaultImg;
        }
    };

    const handleCardHolderChange = (e) => {
        const value = e.target.value;
        setCardHolderName(value);
        if (value.length === 0) {
            setCardHolderError('Cardholder name is required.');
        } else if (value.length < 3) {
            setCardHolderError('At least 3 characters required.');
        } else if (value.length > 26) {
            setCardHolderError('Max 26 characters allowed.');
        } else {
            setCardHolderError('');
        }
    };

    const handleExpiryChange = (event) => {
        if (event.complete) {
            setExpiryError('');
        } else {
            setExpiryError('Invalid expiry date.');
        }
    };

    const handleCvcChange = (event) => {
        if (event.complete) {
            setCvcError('');
        } else {
            setCvcError('Invalid CVC.');
        }
    };

    const handleDeleteCard = async (id) => {
        try {
            await axiosPaymentInstance.delete(`/payment/cards/delete/${id}`);
            fetchSavedCards();
        } catch (error) {
            console.error('Error deleting card:', error);
        } finally {
            setShowDeleteModal(false);
        }
    };

    const handleDeleteClick = (e, card) => {
        e.stopPropagation();
        setSelectedCard(card);
        setShowDeleteModal(true);
    };

    const cancelDelete = () => {
        setSelectedCard(null);
        setShowDeleteModal(false);
    };

    return (
        <>
            <div className="apply-coupon-container">
                <h4 className="add-ons-title">{t('payment.preferred-mode')}</h4>
                <div className="add-ons-line"></div>
                <div className="debit-credit-card">Debit/Credit Cards</div>

                {isLoading && <Loader />}
                {savedCards && (
                    <div className="paymentsaved-buttons-container">
                        <div className="card-list">
                            {savedCards?.map((card) => (
                                <div
                                    key={card?.stripe_card_id}
                                    className={`card-option ${
                                        selectedCard === card?.stripe_card_id ? 'selected' : ''
                                    }`}
                                    onClick={() => setSelectedCard(card?.stripe_card_id)}
                                >
                                    <div className="card-option-details">
                                        <input
                                            type="radio"
                                            name="selectedCard"
                                            value={card?.stripe_card_id}
                                            checked={selectedCard === card?.stripe_card_id}
                                            onChange={() => setSelectedCard(card?.stripe_card_id)}
                                            className="card-radio"
                                        />
                                        <div className="card-icon-image">
                                            <img
                                                src={getCardBrandIcon(card?.card_brand)}
                                                alt={card?.card_brand}
                                            />
                                        </div>
                                        <div className="card-details">
                                            <span className="card-number">
                        XXXX XXXX XXXX {card?.last4}
                                            </span>
                                            <span className="card-expiry">
                        Valid until {card?.exp_month}/{card?.exp_year}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="button-container">
                                        <DeleteIconSvg
                                            onClick={(e) =>
                                                handleDeleteClick(e, card?.stripe_card_id)
                                            }
                                        />
                                    </div>
                                </div>
                            ))}
                            {!savedCards?.length > 0 && (
                                <div className="add-card-button-container">No Saved Cards</div>
                            )}
                        </div>
                    </div>
                )}

                {showDeleteModal && selectedCard && (
                    <DeleteOfferModal
                        open={showDeleteModal}
                        handleClose={cancelDelete}
                        confirmDelete={() => handleDeleteCard(selectedCard)}
                        cancelDelete={cancelDelete}
                        message="Are you sure you want to delete this card?"
                    />
                )}

                {showCardDetails && (
                    <div>
                        <div className="card-details-container">
                            <div className="card-name-container">
                                <h3 className="add-ons-title">Enter Card Details</h3>
                                <CloseIcon onClick={() => setShowCardDetails(false)} />
                            </div>
                            {/* Card Holder Name */}
                            <div className="card-label-input">
                                <label className="card-label-name">Card Holder Name</label>
                                <input
                                    className="card-input-field"
                                    type="text"
                                    name="holderName"
                                    placeholder="Card Holder Name"
                                    value={cardHolderName}
                                    onChange={handleCardHolderChange}
                                />
                                {cardHolderError && (
                                    <p className="card-error-message">{cardHolderError}</p>
                                )}
                            </div>

                            {/* Card Number */}
                            <div className="card-label-input">
                                <label className="card-label-name">Card Number</label>
                                <CardNumberElement
                                    className="Input-Card"
                                    options={CARD_ELEMENT_OPTIONS}
                                />
                            </div>

                            {/* Expiry Date and CVC */}
                            <div className="expiry-and-cvv-container">
                                <div className="card-label-input expiry-date">
                                    <label className="card-label-name">Expiry Date</label>
                                    <CardExpiryElement
                                        className="Input"
                                        options={CARD_ELEMENT_OPTIONS}
                                        onChange={handleExpiryChange}
                                    />
                                    {expiryError && (
                                        <p className="card-error-message">{expiryError}</p>
                                    )}
                                </div>

                                <div className="card-label-input expiry-date">
                                    <label className="card-label-name">CVC</label>
                                    <CardCvcElement
                                        className="Input"
                                        options={CARD_ELEMENT_OPTIONS}
                                        onChange={handleCvcChange}
                                    />
                                    {cvcError && <p className="card-error-message">{cvcError}</p>}
                                </div>
                            </div>

                            <div className="save-btn">
                                <button
                                    className="small-button"
                                    onClick={handleSaveCard}
                                    disabled={loading}
                                >
                                    {loading ? 'Saving Card...' : 'Save Card'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div
                    className="payment-button"
                    onClick={() => handlePaymentMethodChange('addCard')}
                >
                    <img
                        src={addCardImage}
                        alt="addCardImage"
                        style={{ marginRight: '15px' }}
                    />
          Add New Card
                </div>
            </div>

            <button
                className="make-payment-btn"
                onClick={() => {
                    handlePayment();
                }}
                disabled={
                    (paymentMethod === 'card' && !selectedCard) ||
          !stripe ||
          isProcessing ||
          calculationLoading
                }
            >
                {isProcessing ? 'Processing...' : 'Make Payment'}
            </button>
        </>
    );
};

export default PaymentForm;